import { queryOptions, useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { changeActionPlanLevel, changeActionPlanTargetStatus, createActionPlanTarget, createActionPlanTargetComment, editActionPlanTarget, fetchStudentActionPlans, getActionPlanDropdownValues, markReferralAsRead, markReferralAsUnread, referActionPlan } from './api'
import { ActionPlan, ActionPlanReferral, ActionPlanTarget, ActionPlanTargetComment, ActionPlanType, AddActionPlanTargetBody, AddActionPlanTargetCommentBody, ChangeActionPlanLevelBody, ChangeActionPlanTargetStatusBody, EditActionPlanTargetBody, GetActionPlanDropdownValuesSuccess, MarkActionPlanReferralReadBody, MarkActionPlanReferralReadParams, ReferActionPlanBody } from '@godalming/api-types'
import toast from 'react-hot-toast'
import { useParams } from '@tanstack/react-router'

type ActionPlansMutationContext = {
    toastId?: string
    previousActionPlans?: ActionPlan[]
    optimisticTargetId?: number
    optimisticCommentId?: number
    studentNo?: string
}

function matchesActionPlan(actionPlan: ActionPlan, type: string, subjectDesc?: string | null) {
    const matchesType = actionPlan.type === type
    const matchesSubject = type !== 'academic-support'
        || actionPlan.subjectInfo?.description === subjectDesc

    return matchesType && matchesSubject
}

function getTargetStatusText(statusId: number) {
    switch (statusId) {
        case 1:
            return 'Open'
        case 2:
            return 'Completed'
        case 3:
            return 'Not Met'
        case 4:
            return 'Deleted'
        default:
            return 'Unknown'
    }
}

function restoreActionPlans(queryClient: ReturnType<typeof useQueryClient>, studentNo: string | undefined, previousActionPlans: ActionPlan[] | undefined) {
    if (studentNo && previousActionPlans) {
        queryClient.setQueryData(studentActionPlansQueryOptions(studentNo).queryKey, previousActionPlans)
    }
}

function replaceOptimisticTarget(targets: ActionPlanTarget[] | null | undefined, optimisticTargetId: number | undefined, target: ActionPlanTarget) {
    if (!targets) {
        return null
    }

    if (optimisticTargetId === undefined) {
        return targets
    }

    return targets.map((existingTarget) => (
        existingTarget.idStudentActionPlanTarget === optimisticTargetId
            ? target
            : existingTarget
    ))
}

export const studentActionPlansQueryOptions = (studentNo: string) =>
    queryOptions<ActionPlan[]>({
        queryKey: ['students', studentNo, 'actionPlans'],
        queryFn: () => fetchStudentActionPlans(studentNo),
        select: (actionPlans) => {
            return actionPlans.filter((actionPlan) => !(actionPlan as ActionPlan & { isClosed: boolean }).isClosed)
        },
    })

export function useStudentActionPlans(studentNo: string) {
    return useQuery(studentActionPlansQueryOptions(studentNo))
}

export function useStudentActionPlansSuspense(studentNo: string) {
    return useSuspenseQuery(studentActionPlansQueryOptions(studentNo))
}

export const studentActionPlanDropdownValuesQueryOptions = () =>
    queryOptions<GetActionPlanDropdownValuesSuccess>({
        queryKey: ['actionPlanDropdownValues'],
        queryFn: () => getActionPlanDropdownValues(),
        staleTime: 1000 * 60 * 60 * 24, // 1 day
    })

export function useStudentActionPlanDropdownValues() {
    return useQuery(studentActionPlanDropdownValuesQueryOptions())
}

export function useStudentActionPlanDropdownValuesSuspense() {
    return useSuspenseQuery(studentActionPlanDropdownValuesQueryOptions())
}

export function useCreateTargetMutation() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (params: AddActionPlanTargetBody) => createActionPlanTarget(params),
        onMutate: async (params): Promise<ActionPlansMutationContext> => {
            const toastId = toast.loading('Creating target...')
            const queryKey = studentActionPlansQueryOptions(params.studentNo).queryKey
            // await queryClient.cancelQueries({ queryKey })

            // const optimisticTargetId = -Date.now()

            // const optimisticTarget: ActionPlanTarget = {
            //     idStudentActionPlanTarget: optimisticTargetId,
            //     dateStudentActionPlanTargetCreated: new Date().toISOString(),
            //     nameStudentActionPlanStaffTarget: 'You',
            //     studentActionPlanTargetText: params.targetText,
            //     dateStudentActionPlanTargetDeadline: params.dateDeadline,
            //     targetStatus: 'Open',
            //     targetStatusId: 1,
            //     comments: [],
            //     targetType: 'General',
            //     targetTypeId: params.typeId ?? 0,
            // }

            // queryClient.setQueryData<ActionPlan[]>(queryKey, (actionPlans) => {
            //     if (!actionPlans) {
            //         return actionPlans
            //     }

            //     return actionPlans.map((actionPlan) => (
            //         matchesActionPlan(actionPlan, params.type, params.subjectDesc)
            //             ? { ...actionPlan, targets: [...(actionPlan.targets ?? []), optimisticTarget] }
            //             : actionPlan
            //     ))
            // })

            return { toastId }
        },
        onSuccess: async (data, params, context) => {
            toast.success('Target created.', { id: context?.toastId })
            const queryKey = studentActionPlansQueryOptions(params.studentNo).queryKey
            await queryClient.cancelQueries({ queryKey })

            const newTarget: ActionPlanTarget = {
                idStudentActionPlanTarget: data.idStudentActionPlanTarget,
                dateStudentActionPlanTargetCreated: data.dateStudentActionPlanTargetCreated,
                nameStudentActionPlanStaffTarget: data.nameStudentActionPlanStaffTarget,
                studentActionPlanTargetText: data.studentActionPlanTargetText,
                dateStudentActionPlanTargetDeadline: data.dateStudentActionPlanTargetDeadline,
                targetStatus: 'Open',
                targetStatusId: 1,
                comments: [],
                targetType: data.targetType,
                targetTypeId: data.targetTypeId,
            }

            queryClient.setQueryData<ActionPlan[]>(queryKey, (actionPlans) => {
                if (!actionPlans) {
                    console.error("No action plans found in cache when trying to add new target.")
                    return actionPlans
                }

                return actionPlans.map((actionPlan) => (
                    matchesActionPlan(actionPlan, params.type, params.subjectDesc)
                        ? { ...actionPlan, targets: [...(actionPlan.targets ?? []), newTarget] }
                        : actionPlan
                ))
            })
        },
        onError: (err, _vars, context) => {
            restoreActionPlans(queryClient, _vars.studentNo, context?.previousActionPlans)
            toast.error('Error creating target: ' + err.message, { id: context?.toastId })
        },
        onSettled: async (_data, _err, vars) => {
            await new Promise((resolve) => setTimeout(resolve, 500))
            void queryClient.invalidateQueries({ queryKey: studentActionPlansQueryOptions(vars.studentNo).queryKey })
        },
    })
}

export function useEditTargetMutation() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (params: EditActionPlanTargetBody) => editActionPlanTarget(params),
        onMutate: async (params): Promise<ActionPlansMutationContext> => {
            toast.loading('Saving target...', { id: 'edit-target' })
            const queryKey = studentActionPlansQueryOptions(params.studentNo).queryKey
            await queryClient.cancelQueries({ queryKey })

            const previousActionPlans = queryClient.getQueryData<ActionPlan[]>(queryKey)
            const existingTarget = previousActionPlans
                ?.flatMap((actionPlan) => actionPlan.targets ?? [])
                .find((target) => target.idStudentActionPlanTarget === params.targetId)

            if (existingTarget) {
                const updatedTarget: ActionPlanTarget = {
                    ...existingTarget,
                    studentActionPlanTargetText: params.targetText ?? existingTarget.studentActionPlanTargetText,
                    dateStudentActionPlanTargetDeadline: params.targetDeadline ?? existingTarget.dateStudentActionPlanTargetDeadline,
                    targetTypeId: params.changeType ? params.typeId ?? 0 : existingTarget.targetTypeId,
                    targetType: params.changeType && !params.typeId ? 'General' : existingTarget.targetType,
                }

                queryClient.setQueryData<ActionPlan[]>(queryKey, (actionPlans) => {
                    if (!actionPlans) {
                        return actionPlans
                    }

                    return actionPlans.map((actionPlan) => {
                        const isDestinationPlan = matchesActionPlan(actionPlan, params.type, params.subjectDesc)
                        const targetsWithoutEditedTarget = (actionPlan.targets ?? []).filter((target) => (
                            target.idStudentActionPlanTarget !== params.targetId
                        ))

                        if (isDestinationPlan) {
                            return {
                                ...actionPlan,
                                targets: [...targetsWithoutEditedTarget, updatedTarget],
                            }
                        }

                        return {
                            ...actionPlan,
                            targets: targetsWithoutEditedTarget,
                        }
                    })
                })
            }

            return { previousActionPlans, studentNo: params.studentNo }
        },
        onSuccess: async (data, params, context) => {
            toast.success('Target saved.', { id: 'edit-target' })
            void queryClient.invalidateQueries({ queryKey: studentActionPlansQueryOptions(params.studentNo).queryKey })
        },
        onError: (err, _params, context) => {
            restoreActionPlans(queryClient, _params.studentNo, context?.previousActionPlans)
            toast.error('Error saving target: ' + err.message, { id: 'edit-target' })
        },
        onSettled: (_data, _err, vars) => {
            void queryClient.invalidateQueries({ queryKey: studentActionPlansQueryOptions(vars.studentNo).queryKey })
        },
    })
}

export function useChangeTargetStatus() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (params: ChangeActionPlanTargetStatusBody) => changeActionPlanTargetStatus(params),
        onMutate: async (params): Promise<ActionPlansMutationContext> => {
            const queryKey = studentActionPlansQueryOptions(params.studentNo).queryKey
            await queryClient.cancelQueries({ queryKey })

            const previousActionPlans = queryClient.getQueryData<ActionPlan[]>(queryKey)

            queryClient.setQueryData<ActionPlan[]>(queryKey, (actionPlans) => {
                if (!actionPlans) {
                    return actionPlans
                }

                return actionPlans.map((actionPlan) => ({
                    ...actionPlan,
                    targets: actionPlan.targets?.map((target) => (
                        target.idStudentActionPlanTarget === params.targetId
                            ? {
                                ...target,
                                targetStatusId: params.statusId,
                                targetStatus: getTargetStatusText(params.statusId),
                            }
                            : target
                    )) ?? actionPlan.targets,
                }))
            })

            return { previousActionPlans, studentNo: params.studentNo }
        },
        onSuccess: async (data, params, context) => {
            void queryClient.invalidateQueries({ queryKey: studentActionPlansQueryOptions(params.studentNo).queryKey })
        },
        onError: (err, _params, context) => {
            restoreActionPlans(queryClient, _params.studentNo, context?.previousActionPlans)
            toast.error('Error changing target status: ' + err.message, { id: 'change-target-status' })
        },
        onSettled: (_data, _err, params) => {

        },
    })
}

export function useAddTargetComment() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (params: AddActionPlanTargetCommentBody) => createActionPlanTargetComment(params),
        onMutate: async (params): Promise<ActionPlansMutationContext> => {
            const toastId = toast.loading('Adding comment...')
            const queryKey = studentActionPlansQueryOptions(params.studentNo).queryKey
            await queryClient.cancelQueries({ queryKey })

            const previousActionPlans = queryClient.getQueryData<ActionPlan[]>(queryKey)
            const optimisticCommentId = -Date.now()

            const optimisticComment: ActionPlanTargetComment = {
                idStudentActionPlanTargetComment: optimisticCommentId,
                targetCommentTargetId: params.targetId,
                dateStudentActionPlanTargetCommentCreated: new Date().toISOString(),
                initialsStudentActionPlanStaffTargetComment: 'You',
                studentActionPlanTargetCommentText: params.commentText,
            }

            queryClient.setQueryData<ActionPlan[]>(queryKey, (actionPlans) => {
                if (!actionPlans) {
                    return actionPlans
                }

                return actionPlans.map((actionPlan) => ({
                    ...actionPlan,
                    targets: actionPlan.targets?.map((target) => (
                        target.idStudentActionPlanTarget === params.targetId
                            ? { ...target, comments: [...(target.comments ?? []), optimisticComment] }
                            : target
                    )) ?? actionPlan.targets,
                }))
            })

            return { toastId, previousActionPlans, optimisticCommentId, studentNo: params.studentNo }
        },
        onSuccess: async (data, params, context) => {
            toast.success('Comment added.', { id: context?.toastId })

            if (data && typeof data === 'object' && 'idStudentActionPlanTargetComment' in data) {
                const queryKey = studentActionPlansQueryOptions(params.studentNo).queryKey

                queryClient.setQueryData<ActionPlan[]>(queryKey, (actionPlans) => {
                    if (!actionPlans) {
                        return actionPlans
                    }

                    return actionPlans.map((actionPlan) => ({
                        ...actionPlan,
                        targets: actionPlan.targets?.map((target) => (
                            target.idStudentActionPlanTarget === params.targetId
                                ? {
                                    ...target,
                                    comments: target.comments?.map((comment) => (
                                        comment.idStudentActionPlanTargetComment === context?.optimisticCommentId
                                            ? data as ActionPlanTargetComment
                                            : comment
                                    )) ?? target.comments,
                                }
                                : target
                        )) ?? actionPlan.targets,
                    }))
                })
            }
        },
        onError: (err, _params, context) => {
            restoreActionPlans(queryClient, _params.studentNo, context?.previousActionPlans)
            toast.error('Error adding comment: ' + err.message, { id: context?.toastId })
        },
        onSettled: (_data, _err, params) => {
            void queryClient.invalidateQueries({ queryKey: studentActionPlansQueryOptions(params.studentNo).queryKey })
        },
    })
}

export function useReferActionPlan() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (params: ReferActionPlanBody) => referActionPlan(params),
        onMutate: async (params): Promise<ActionPlansMutationContext> => {
            const toastId = toast.loading('Referring action plan...')
            const queryKey = studentActionPlansQueryOptions(params.studentNo).queryKey
            await queryClient.cancelQueries({ queryKey })

            const previousActionPlans = queryClient.getQueryData<ActionPlan[]>(queryKey)
            const sourceActionPlan = previousActionPlans?.find((actionPlan) => actionPlan.type === params.typeSource)

            const optimisticReferral: ActionPlanReferral = {
                id: -Date.now(),
                level: params.referralLevelDest,
                sourceType: params.typeSource as ActionPlanType,
                sourceSubject: sourceActionPlan?.subjectInfo?.description ?? '',
                createdBy: 'You',
                note: params.referralNote ?? '',
                dateCreated: new Date().toISOString(),
                isRead: false,
            }

            queryClient.setQueryData<ActionPlan[]>(queryKey, (actionPlans) => {
                if (!actionPlans) {
                    return actionPlans
                }

                return actionPlans.map((actionPlan) => (
                    actionPlan.type === params.typeDest
                        ? { ...actionPlan, referrals: [...(actionPlan.referrals ?? []), optimisticReferral] }
                        : actionPlan
                ))
            })

            return { toastId, previousActionPlans, studentNo: params.studentNo }
        },
        onSuccess: async (data, params, context) => {
            toast.success('Action plan referral submitted.', { id: context?.toastId })
        },
        onError: (err, _params, context) => {
            restoreActionPlans(queryClient, _params.studentNo, context?.previousActionPlans)
            toast.error('Error referring action plan: ' + err.message, { id: context?.toastId })
        },
        onSettled: (_data, _err, params) => {
            void queryClient.invalidateQueries({ queryKey: studentActionPlansQueryOptions(params.studentNo).queryKey })
        },
    })
}

export function useMarkReferralAsRead() {
    const { studentNo } = useParams({ strict: false })
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (params: MarkActionPlanReferralReadBody) => markReferralAsRead(params),
        onMutate: async (params): Promise<ActionPlansMutationContext> => {
            const toastId = toast.loading('Marking referral as read...')

            if (!studentNo) {
                return { toastId }
            }

            const queryKey = studentActionPlansQueryOptions(studentNo).queryKey
            await queryClient.cancelQueries({ queryKey })

            const previousActionPlans = queryClient.getQueryData<ActionPlan[]>(queryKey)

            queryClient.setQueryData<ActionPlan[]>(queryKey, (actionPlans) => {
                if (!actionPlans) {
                    return actionPlans
                }

                return actionPlans.map((actionPlan) => ({
                    ...actionPlan,
                    referrals: actionPlan.referrals?.map((referral) => (
                        referral.id === params.id
                            ? {
                                ...referral,
                                isRead: true,
                                readNote: params.readNote ?? undefined,
                                readBy: referral.readBy ?? 'You',
                                dateRead: referral.dateRead ?? new Date().toISOString(),
                            }
                            : referral
                    )) ?? actionPlan.referrals,
                }))
            })

            return { toastId, previousActionPlans, studentNo }
        },
        onSuccess: async (data, params, context) => {
            toast.success('Referral marked as read.', { id: context?.toastId })
        },
        onError: (err, _params, context) => {
            restoreActionPlans(queryClient, studentNo, context?.previousActionPlans)
            toast.error('Error marking referral as read: ' + err.message, { id: context?.toastId })
        },
        onSettled: (_data, _err, params) => {
            if (studentNo) {
                queryClient.invalidateQueries({ queryKey: studentActionPlansQueryOptions(studentNo).queryKey })
            }
        },
    })
}

export function useMarkReferralAsUnread() {

    const { studentNo } = useParams({ strict: false })
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (params: MarkActionPlanReferralReadBody) => markReferralAsUnread(params),
        onMutate: async (params): Promise<ActionPlansMutationContext> => {
            const toastId = toast.loading('Marking referral as unread...')

            if (!studentNo) {
                return { toastId }
            }

            const queryKey = studentActionPlansQueryOptions(studentNo).queryKey
            await queryClient.cancelQueries({ queryKey })

            const previousActionPlans = queryClient.getQueryData<ActionPlan[]>(queryKey)

            queryClient.setQueryData<ActionPlan[]>(queryKey, (actionPlans) => {
                if (!actionPlans) {
                    return actionPlans
                }

                return actionPlans.map((actionPlan) => ({
                    ...actionPlan,
                    referrals: actionPlan.referrals?.map((referral) => (
                        referral.id === params.id
                            ? {
                                ...referral,
                                isRead: false,
                                readNote: undefined,
                                readBy: undefined,
                                dateRead: undefined,
                            }
                            : referral
                    )) ?? actionPlan.referrals,
                }))
            })

            return { toastId, previousActionPlans, studentNo }
        },
        onSuccess: async (data, params, context) => {
            toast.success('Referral marked as unread.', { id: context?.toastId })
        },
        onError: (err, _params, context) => {
            restoreActionPlans(queryClient, studentNo, context?.previousActionPlans)
            toast.error('Error marking referral as unread: ' + err.message, { id: context?.toastId })
        },
        onSettled: (_data, _err, params) => {
            if (studentNo) {
                queryClient.invalidateQueries({ queryKey: studentActionPlansQueryOptions(studentNo).queryKey })
            }
        },
    })
}

export function useChangeActionPlanLevel() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (params: ChangeActionPlanLevelBody) => changeActionPlanLevel(params),
        onMutate: async (params) => {
            const toastId = toast.loading('Changing action plan level...')
            const queryKey = studentActionPlansQueryOptions(params.studentNo).queryKey
            await queryClient.cancelQueries({ queryKey })

            const previousActionPlans = queryClient.getQueryData<ActionPlan[]>(queryKey)

            queryClient.setQueryData<ActionPlan[]>(queryKey, (actionPlans) => {
                if (!actionPlans) {
                    return actionPlans
                }

                return actionPlans.map((actionPlan) => {
                    const matchesType = actionPlan.type === params.type
                    const matchesSubject = params.type !== 'academic-support'
                        || actionPlan.subjectInfo?.description === params.subjectDesc

                    return matchesType && matchesSubject
                        ? { ...actionPlan, level: params.level === 0 ? null : params.level }
                        : actionPlan
                })
            })

            return { toastId, previousActionPlans }
        },
        onSuccess: async (data, params, context) => {
            toast.success('Action plan level changed.', { id: context?.toastId })

            const queryKey = studentActionPlansQueryOptions(params.studentNo).queryKey
            void queryClient.invalidateQueries({ queryKey })
        },
        onError: (err, _params, context) => {
            if (context?.previousActionPlans) {
                queryClient.setQueryData(studentActionPlansQueryOptions(_params.studentNo).queryKey, context.previousActionPlans)
            }

            toast.error('Error changing action plan level: ' + err.message, { id: context?.toastId })
        },
        onSettled: (_data, _err, params) => {

        },
    })
}

export function useCreateActionPlan() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (params: ChangeActionPlanLevelBody) => {
            return changeActionPlanLevel({ ...params, level: 1 })
        },
        onMutate: async (params) => {
            const toastId = toast.loading('Creating action plan...')
            const queryKey = studentActionPlansQueryOptions(params.studentNo).queryKey
            await queryClient.cancelQueries({ queryKey })

            const previousActionPlans = queryClient.getQueryData<ActionPlan[]>(queryKey)

            queryClient.setQueryData<ActionPlan[]>(queryKey, (actionPlans) => {
                if (!actionPlans) {
                    return actionPlans
                }

                return actionPlans.map((actionPlan) => {
                    const matchesType = actionPlan.type === params.type
                    const matchesSubject = params.type !== 'academic-support'
                        || actionPlan.subjectInfo?.description === params.subjectDesc

                    return matchesType && matchesSubject
                        ? { ...actionPlan, level: 1 }
                        : actionPlan
                })
            })

            return { toastId, previousActionPlans }
        },
        onSuccess: async (data, params, context) => {
            toast.success('Action plan created.', { id: context?.toastId })
        },
        onError: (err, _params, context) => {
            if (context?.previousActionPlans) {
                queryClient.setQueryData(studentActionPlansQueryOptions(_params.studentNo).queryKey, context.previousActionPlans)
            }

            toast.error('Error creating action plan: ' + err.message, { id: context?.toastId })
        },
        onSettled: async (_data, _err, params) => {
            // set timeout
            await new Promise((resolve) => setTimeout(resolve, 500))
            const queryKey = studentActionPlansQueryOptions(params.studentNo).queryKey
            queryClient.invalidateQueries({ queryKey })
        },
    })
}
